<template>
  <Head title="AI Image Generator" />
  <div v-if="initializing" class="flex h-full items-center justify-center bg-white">
    <div class="h-8 w-8 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600"></div>
  </div>

  <div v-else class="flex h-full flex-col overflow-hidden bg-white text-gray-900">
    <header class="flex items-center justify-between border-b border-gray-200 px-4 py-3">
      <div class="flex items-center gap-2">
        <span class="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-100 text-violet-600">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.6-4.6a2 2 0 012.8 0L16 16m-2-2 1.6-1.6a2 2 0 012.8 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
        </span>
        <div>
          <div class="text-sm font-semibold">{{ header_title }}</div>
          <div v-if="header_subtitle" class="text-xs text-gray-500">{{ header_subtitle }}</div>
        </div>
      </div>
      <span class="rounded-full bg-gray-100 px-3 py-1 text-xs">{{ successfulCount }}/{{ generation_limit }}</span>
    </header>

    <main ref="scrollArea" class="min-h-0 flex-1 overflow-y-auto">
      <div class="mx-auto max-w-3xl space-y-6 px-4 py-6">
        <div v-if="generations.length === 0" class="py-12 text-center">
          <h1 class="text-2xl font-semibold">{{ welcome_title }}</h1>
          <p class="mt-2 text-sm text-gray-500">{{ welcome_text }}</p>
        </div>
        <article v-for="item in generations" :key="item.id" class="space-y-3">
          <div class="ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-gray-900 px-4 py-3 text-sm text-white">{{ item.prompt }}</div>
          <div class="max-w-[90%] rounded-2xl rounded-bl-md border border-gray-200 bg-gray-50 p-3">
            <div v-if="item.status === 'processing'" class="flex items-center justify-center rounded-xl bg-gray-100" style="min-height: 12rem;">
              <div class="text-center"><div class="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600"></div><p class="mt-3 text-sm text-gray-500">Создаём изображение…</p></div>
            </div>
            <a v-else-if="item.status === 'finished'" :href="item.image_url" target="_blank" rel="noopener noreferrer" class="block cursor-zoom-in rounded-xl">
              <img :src="item.image_url" :alt="item.prompt" class="max-h-[32rem] w-full rounded-xl object-contain" @load="scrollBottom" />
            </a>
            <div v-else-if="item.status === 'limit'" class="prose prose-sm max-w-none rounded-xl bg-white p-2 text-gray-700" v-html="item.message" @click="trackRegistrationClick"></div>
            <div v-else class="rounded-xl bg-red-50 p-4 text-sm text-red-700">{{ item.error || 'Не удалось создать изображение' }}</div>
          </div>
        </article>
      </div>
    </main>

    <footer class="border-t border-gray-200 bg-white p-4">
      <form class="relative mx-auto max-w-3xl space-y-3" @submit.prevent="generate">
        <div v-if="submitting" class="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-white/90 backdrop-blur-sm">
          <div class="flex items-center gap-3 rounded-xl bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200">
            <span class="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600"></span>
            <span>{{ sending_text }}</span>
          </div>
        </div>
        <div v-if="previewUrl" class="relative inline-block">
          <img :src="previewUrl" class="h-20 w-20 rounded-xl border object-cover" alt="Reference" />
          <button type="button" @click="clearReference" class="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-gray-900 text-sm text-white">×</button>
        </div>
        <div v-if="error" class="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{{ error }}</div>
        <div class="flex gap-2">
          <select v-model="aspectRatio" class="rounded-xl border-gray-200 bg-gray-50 text-sm focus:border-violet-400 focus:ring-violet-400">
            <option value="1:1">1:1</option><option value="16:9">16:9</option><option value="9:16">9:16</option>
          </select>
          <label class="flex cursor-pointer items-center justify-center rounded-xl bg-gray-100 px-3 text-gray-600 hover:bg-gray-200" title="Добавить фото">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.6-4.6a2 2 0 012.8 0L16 16m-2-2 1.6-1.6a2 2 0 012.8 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            <input type="file" accept="image/png,image/jpeg,image/jfif,.jfif,image/webp" class="hidden" @change="selectReference" />
          </label>
          <textarea v-model="prompt" rows="1" minlength="3" maxlength="16000" placeholder="Опишите изображение…" class="min-h-[46px] flex-1 resize-none rounded-2xl border-0 bg-gray-100 px-4 py-3 text-sm focus:ring-2 focus:ring-violet-300" @keydown.enter.exact.prevent="generate"></textarea>
          <button :disabled="!canSubmit" class="self-end rounded-xl bg-violet-600 p-3 text-white disabled:cursor-not-allowed disabled:opacity-40">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0 7 7m-7-7v18"/></svg>
          </button>
        </div>
        <div v-if="captcha_enabled" ref="captchaContainer" class="min-h-[100px]"></div>
      </form>
    </footer>

  </div>
</template>

<script setup>
import { Head } from '@inertiajs/vue3'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { createConsumer } from '@rails/actioncable'
import { getUserFingerprint } from '../../lib/fingerprint.js'
import { ImageValidationError, optimizeImage } from '../../lib/image_optimizer.js'

const props = defineProps({
  header_title: { type: String, default: 'AI Academy Images' },
  header_subtitle: { type: String, default: null },
  welcome_title: { type: String, default: 'Создайте изображение' },
  welcome_text: { type: String, default: 'Опишите идею или добавьте одно референсное фото' },
  prompt: { type: String, default: null },
  limit_message: { type: String, default: null },
  sending_text: { type: String, default: 'Sending...' },
  captcha_enabled: Boolean,
  captcha_client_key: String,
  captcha_language: String,
  generation_limit: { type: Number, default: 5 }
})
const trackEvent = (event) => {
  window.parent.postMessage({type: 'aiacademy_widget_event', event, widget_type: 'image'}, 'https://aiacademy.me')
}
const fingerprint = ref(''), generations = ref([]), successfulCount = ref(0), initializing = ref(true)
const prompt = ref(props.prompt || ''), aspectRatio = ref('1:1'), reference = ref(null), previewUrl = ref(null), submitting = ref(false), error = ref('')
const captchaContainer = ref(null), captchaToken = ref(''), captchaId = ref(null), scrollArea = ref(null)
const consumer = createConsumer(), subscriptions = new Map()
const canSubmit = computed(() => prompt.value.trim().length >= 3 && !submitting.value && (!props.captcha_enabled || captchaToken.value))

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content
const subscribe = (item) => {
  if (item.status !== 'processing' || subscriptions.has(item.id)) return
  const subscription = consumer.subscriptions.create({channel: 'WidgetImageChannel', generation_id: item.id, fingerprint: fingerprint.value}, {received(data) {
    if (data.type !== 'image_generation') return
    if (data.generation.status === 'finished') trackEvent('widget_success')
    const index = generations.value.findIndex(g => g.id === data.generation.id)
    if (index >= 0) generations.value[index] = data.generation
    successfulCount.value = generations.value.filter(g => g.status === 'finished').length
    subscription.unsubscribe(); subscriptions.delete(item.id)
    nextTick(scrollBottom)
  }})
  subscriptions.set(item.id, subscription)
}
const load = async () => {
  fingerprint.value = await getUserFingerprint()
  const response = await fetch(`/api/widget/image_generations?fingerprint=${encodeURIComponent(fingerprint.value)}`)
  const data = await response.json(); generations.value = data.generations || []; successfulCount.value = data.successful_count || 0
  generations.value.forEach(subscribe); initializing.value = false; nextTick(() => { scrollBottom(); renderCaptcha() })
}
const generate = async () => {
  if (!canSubmit.value) return
  trackEvent('widget_start')
  submitting.value = true; error.value = ''
  const submittedPrompt = prompt.value.trim()
  const body = new FormData(); body.append('fingerprint', fingerprint.value); body.append('prompt', submittedPrompt); body.append('aspect_ratio', aspectRatio.value)
  if (reference.value) body.append('reference_image', reference.value); if (props.captcha_enabled) body.append('smart-token', captchaToken.value)
  try {
    const response = await fetch('/api/widget/image_generations', {method: 'POST', headers: {'X-CSRF-Token': csrf()}, body})
    const data = await response.json()
    if (!response.ok && data.should_register) {
      trackEvent('widget_limit')
      generations.value.push({
        id: `limit-${Date.now()}`,
        prompt: submittedPrompt,
        status: 'limit',
        message: props.limit_message || data.error || 'Вы достигли лимита генераций. Пожалуйста, зарегистрируйтесь, чтобы продолжить.'
      })
      prompt.value = ''; clearReference(); nextTick(scrollBottom)
      return
    }
    if (!response.ok) throw new Error(data.error)
    generations.value.push(data.generation); subscribe(data.generation); prompt.value = ''; clearReference(); nextTick(scrollBottom)
  } catch (e) { error.value = e.message } finally { submitting.value = false; resetCaptcha() }
}
const trackRegistrationClick = (event) => {
  const link = event.target?.closest?.('a[href]')
  if (!link) return

  const url = new URL(link.href)
  if (url.origin === 'https://chat.aiacademy.me' && url.pathname === '/register') trackEvent('widget_register_click')
}
const selectReference = async (event) => {
  const selectedFile = event.target.files?.[0]
  if (!selectedFile) return
  if (selectedFile.size > 10 * 1024 * 1024) { error.value = 'Максимальный размер файла — 10 МБ'; return }

  try {
    const file = await optimizeImage(selectedFile)
    clearReference()
    reference.value = file
    previewUrl.value = URL.createObjectURL(file)
  } catch (uploadError) {
    error.value = uploadError instanceof ImageValidationError ? uploadError.message : 'Не удалось обработать изображение'
  }
}
const clearReference = () => { if (previewUrl.value) URL.revokeObjectURL(previewUrl.value); previewUrl.value = null; reference.value = null }
const scrollBottom = () => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (scrollArea.value) scrollArea.value.scrollTop = scrollArea.value.scrollHeight
    })
  })
}
const renderCaptcha = () => { if (!props.captcha_enabled || !captchaContainer.value) return; if (!window.smartCaptcha) return setTimeout(renderCaptcha, 100); captchaId.value = window.smartCaptcha.render(captchaContainer.value, {sitekey: props.captcha_client_key, hl: props.captcha_language, callback: token => captchaToken.value = token, 'expired-callback': () => captchaToken.value = ''}) }
const resetCaptcha = () => { captchaToken.value = ''; if (captchaId.value != null) window.smartCaptcha?.reset(captchaId.value) }
onMounted(load)
onBeforeUnmount(() => { subscriptions.forEach(s => s.unsubscribe()); consumer.disconnect(); clearReference(); if (captchaId.value != null) window.smartCaptcha?.destroy(captchaId.value) })
</script>
